import React, { SyntheticEvent, useMemo, useState } from 'react';
import { Accept } from 'react-dropzone/.';
import { Controller, useFormContext } from 'react-hook-form';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { Stack, styled } from '@mui/material';
import Iconify from 'src/components/iconify';
import FileUpload from 'src/components/upload/FileUpload';
import { toFileItem } from 'src/utils/fileUploadUtils';

import FullScreenViewer from './sections/FullScreenViewer';
import MediaRenderer from './sections/MediaRenderer';

interface IRHFUploadImageProps {
  name: string;
  files: Array<File | string>; // 기존 파일 (URL 또는 File)
  maxFiles?: number; // 최대 파일 수
  // accept?: Record<string, any>; // 허용 파일 타입
  accept?: Accept; // 허용 파일 타입
  onChange: (files: Array<File | string>) => void; // 파일 변경 콜백
}

const RHFUploadImage: React.FC<IRHFUploadImageProps> = ({
  name,
  files = [],
  maxFiles = Infinity,
  accept = {
    'image/jpeg': [],
    'image/jpg': [],
    'image/png': [],
    'image/svg+xml': [],
  },
  onChange,
  ...other
}) => {
  const { control } = useFormContext();
  // 이미지 클릭 시 확대
  const [previewIndex, setPreviewIndex] = useState(-1);

  // 드래그 앤 드롭 순서 변경 처리
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const reorderedFiles = [...files];
    const [movedFile] = reorderedFiles.splice(result.source.index, 1);
    reorderedFiles.splice(result.destination?.index ?? 0, 0, movedFile);
    onChange(reorderedFiles);
  };

  // 이미지 프리뷰 렌더링
  const transformedFiles = useMemo(() => {
    return files.map((file, index) => toFileItem(file, index));
  }, [files]);

  // 파일 제거
  const handleRemoveFile = (e: SyntheticEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    const newFiles = files.filter((_, i) => i !== index);
    onChange(newFiles);
  };

  // 파일 추가
  const handleDropFiles = (newFiles: File[]) => {
    const combinedFiles = [...files, ...newFiles];
    if (combinedFiles.length > maxFiles) {
      alert(`업로드 가능한 파일 수는 ${maxFiles}개 입니다.`);
      return;
    }
    onChange(combinedFiles);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        return (
          <Container {...other}>
            {transformedFiles.length > 0 && (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="files" direction="horizontal">
                  {(provided) => (
                    <DroppableGrid ref={provided.innerRef} {...provided.droppableProps}>
                      {transformedFiles.map((fileItem, index) => {
                        return (
                          <Draggable key={fileItem.id} draggableId={fileItem.id} index={index}>
                            {(provided) => (
                              <MediaBox
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                // 이미지 클릭 시 확대 이벤트 처리
                                onClick={() => setPreviewIndex(index)}
                              >
                                {/* 파일 미리보기 media */}
                                <PreviewContainer>
                                  <MediaRenderer key={fileItem.id} file={fileItem} />
                                </PreviewContainer>
                                {/* 파일 미리보기 delete icon button */}
                                <DeleteButton onClick={(e) => handleRemoveFile(e, index)}>
                                  <DeleteIcon icon="eva:close-fill" />
                                </DeleteButton>
                              </MediaBox>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </DroppableGrid>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            {/* 새로운 파일 업로드 */}
            {files.length < maxFiles && (
              <Box>
                <FileUpload
                  files={[]}
                  showPreview
                  showFileIcon={false}
                  onDrop={handleDropFiles}
                  accept={accept}
                />
              </Box>
            )}

            {/* 파일 프리뷰 모달 (확대) */}
            <FullScreenViewer
              files={files}
              index={previewIndex}
              isOpen={previewIndex !== -1}
              onClose={() => setPreviewIndex(-1)}
              accept={accept}
            />
          </Container>
        );
      }}
    />
  );
};

export default RHFUploadImage;

const Container = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  width: '100%',
}));

const Box = styled('div')(({ theme }) => ({
  flexShrink: 0,
  width: 150,
  height: 150,
}));

const MediaBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 1,
  '& video': {
    pointerEvents: 'none',
  },
}));

const DeleteButton = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 1,
  width: 30,
  height: 30,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.common.black,
  borderRadius: '50%',
  cursor: 'pointer',
}));

const DeleteIcon = styled(Iconify)(({ theme }) => ({
  width: 20,
  color: theme.palette.common.white,
}));
// preview
const PreviewContainer = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.spacing(1),
}));

const DroppableGrid = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  minHeight: '100px',
}));
